import { useModuleStorage } from "@artis/sdk-react";
import { useEffect, useState } from "react";

import { RestrictedAccessMessage } from "../../webapp/components/RestrictedAccessMessage";
import type { PersistedWorkbook } from "@package/scratchpad-utils";
import { loadAutosaved } from "./hooks/useAutosave";
import Scratchpad from "./Scratchpad";
import { useUserSubscriptionTier } from "../../data/user";

/** Runs once when the module type is registered. */
export const init = () => {};

type LoadState = { status: "loading" } | { status: "ready"; initial: PersistedWorkbook | null } | { status: "error" };

export default function ScratchpadModule() {
  const storage = useModuleStorage("user");
  const { disabledFeatures } = useUserSubscriptionTier();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt is a retry nonce that re-runs the load
  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    loadAutosaved(storage).then(
      (initial) => {
        if (!cancelled) setState({ status: "ready", initial });
      },
      // The user-scope get rejects when the server is unreachable. Do not fall back to an empty workbook: mounting the editor would let the first autosave stomp server data.
      () => {
        if (!cancelled) setState({ status: "error" });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [storage, attempt]);

  // Scratchpad is only available to Professional and above (and internal) tiers.
  if (disabledFeatures?.scratchpad) {
    return (
      <div className="flex h-full w-full flex-col" data-testid="scratchpad-restricted">
        <RestrictedAccessMessage
          title="Scratchpad access restricted"
          message="Scratchpad is only available with a Professional subscription. Please contact support to learn more."
          actions={[
            {
              label: "Contact Support",
              onClick: () => window.open("mailto:support@artis.works"),
              variant: "tertiary",
            },
          ]}
        />
      </div>
    );
  }

  if (state.status === "loading") {
    return <div className="flex h-full w-full items-center justify-center text-sm opacity-60">Loading…</div>;
  }
  if (state.status === "error") {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-sm opacity-60">
        <span>Couldn't load scratchpad.</span>
        <button type="button" className="underline" onClick={() => setAttempt((n) => n + 1)}>
          Retry
        </button>
      </div>
    );
  }
  return <Scratchpad storage={storage} initial={state.initial} />;
}
